// Plan 08-05 — ETag polling fallback for the match state path.
//
// Activated by `MatchWsClient.onFallback()` after 3 failed reconnects.
//
// Cadence: 30s default (NY-08-06 staleness boundary — `ny_08_06_polling_*`
// tests pin 304 short-circuit AND state-change-detection-within-30s).
//
// Wire contract (backend Plan 04-03 — `apps/backend/src/match/handlers/state.ts`):
//   - GET /api/match/{id}/state with `If-None-Match: <last-etag>`.
//   - 304 → no body, ETag echoed; client treats as "no change".
//   - 200 → body `{ your_state: PlayerView, status: string }`, fresh ETag.
//   - 401 → clearAuth() + onError; AUTH_INVALID propagates.
//   - 5xx / other non-OK → onError, no state update.
// fix(match) #54 — `status` in the 200 body lets polling clients detect
//   terminal matches and fire the end-of-match overlay via `onStatus`.
//
// @pitfall-10
// Backend ETag (Phase 4 Plan 04-03) computes:
//   `W/"${match_id}-${current_turn_idx}-${slot}"`
// This DOES NOT include `bot_substituted_at` or `missed_turns`. A match where
// the current player has been bot-substituted but no turn has yet committed
// will return identical ETags → polling clients miss the substitution event
// until the next turn submit changes `current_turn_idx`. Tracked as a Phase 4
// follow-up in `Docs/history/deferred-items.md`
// (`P9-DEF-08-05-ETAG-DRIFT`); Phase 9 close-out plan owns the backend fix.

import type { PlayerView } from '../game/PlayerViewSchema'
import { clearAnonSession, effectiveBearerJwt } from '../stores/anonSession'
import { clearAuth } from '../stores/auth'

export interface PollingState {
  running: boolean
  lastEtag: string | null
  intervalId: ReturnType<typeof setInterval> | null
}

export interface PollingOptions {
  onUpdate: (view: PlayerView) => void
  onError: (err: unknown) => void
  /**
   * Called with the match-row `status` from each 200 body (#54). Fires on
   * EVERY 200 that includes status (e.g. 'active' too), not only terminal —
   * callers gate on terminal themselves. NOT called on 304, so ETag staleness
   * can defer a terminal status until the next changed frame (see @pitfall-10).
   */
  onStatus?: (status: string) => void
  /** Cadence in ms. Default 30_000 (NY-08-06 staleness boundary). */
  intervalMs?: number
}

const V1 = 'application/vnd.runegambit.v1+json'

function getApiBaseUrl(): string {
  const url = (import.meta.env?.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')
  if (!url || url.startsWith('<')) {
    throw new Error('VITE_API_BASE_URL not configured')
  }
  return url
}

interface StateResponseBody {
  your_state: PlayerView
  status?: string
}

async function tick(
  matchId: string,
  state: PollingState,
  opts: PollingOptions,
): Promise<void> {
  if (!state.running) return
  const headers: Record<string, string> = { Accept: V1 }
  if (state.lastEtag) headers['If-None-Match'] = state.lastEtag
  // ARCA-93: real bearer if authenticated, else the anon practice JWT so a
  // guest's bot match keeps polling when the WS path falls back.
  const jwt = effectiveBearerJwt()
  if (jwt) headers.Authorization = `Bearer ${jwt}`
  const url = `${getApiBaseUrl()}/api/match/${matchId}/state`
  let res: Response
  try {
    res = await fetch(url, { method: 'GET', headers })
  } catch (e) {
    opts.onError(e instanceof Error ? e : new Error(String(e)))
    return
  }

  if (res.status === 304) {
    // No change — short-circuit, keep lastEtag intact.
    return
  }

  if (res.status === 401) {
    // Auth gone — clear bearer + surface AUTH_INVALID. Phase 2 oracle
    // hardening returns byte-equal ErrorEnvelopes across all 401 sub-causes.
    clearAuth()
    clearAnonSession()
    opts.onError(new Error('AUTH_INVALID'))
    return
  }

  if (!res.ok) {
    opts.onError(new Error(`POLL_${res.status}`))
    return
  }

  // 2xx success — capture fresh ETag, parse body.
  const fresh = res.headers.get('ETag')
  if (fresh) state.lastEtag = fresh
  let body: StateResponseBody
  try {
    body = (await res.json()) as StateResponseBody
  } catch (e) {
    opts.onError(e instanceof Error ? e : new Error(String(e)))
    return
  }

  if (body && typeof body === 'object' && 'your_state' in body) {
    opts.onUpdate(body.your_state)
    // fix(match) #54 — surface match status to the caller so terminal matches
    // can fire the end-of-match overlay even when the WS has dropped.
    if (typeof body.status === 'string') {
      opts.onStatus?.(body.status)
    }
  } else {
    opts.onError(new Error('POLL_BODY_SHAPE'))
  }
}

/**
 * Start a polling loop. Fires an immediate first poll, then continues at
 * `intervalMs` cadence (default 30s — NY-08-06).
 *
 * Returns a `PollingState` handle to feed `stopPolling()`.
 */
export function startPolling(
  matchId: string,
  opts: PollingOptions,
): PollingState {
  const state: PollingState = {
    running: true,
    lastEtag: null,
    intervalId: null,
  }
  const cadence = opts.intervalMs ?? 30_000

  // Schedule recurring polls.
  state.intervalId = setInterval(() => {
    void tick(matchId, state, opts)
  }, cadence)
  // Immediate first poll (do not wait for the first interval to elapse).
  void tick(matchId, state, opts)

  return state
}

/** Stop the polling loop and release the timer. */
export function stopPolling(state: PollingState): void {
  state.running = false
  if (state.intervalId !== null) {
    clearInterval(state.intervalId)
    state.intervalId = null
  }
}
